import {
  SignalOriginSchema,
  TargetTypeSchema,
  type SignalOriginType,
  type TargetTypeType,
} from '../generated';
import {
  AGENTIC_AI_FLOW_SIGNAL_CATEGORIES,
  AGENTIC_AI_NODE_SIGNAL_CATEGORIES,
  AGENTIC_AI_SIGNAL_CATEGORIES,
  AI_APPLICATION_SIGNAL_CATEGORIES,
  EXTERNAL_NETWORK_SIGNAL_CATEGORIES,
  INTERNAL_NETWORK_SIGNAL_CATEGORIES,
  MCP_SERVER_SIGNAL_CATEGORIES,
  WEB_APP_SIGNAL_CATEGORIES,
  isAgenticAiSignalCategory,
  isAiApplicationSignalCategory,
  isExternalNetworkSignalUiCategory,
  isInternalNetworkSignalCategory,
  isMcpServerSignalCategory,
  isWebAppCategory,
} from './signal-category.groups';
import {
  SignalCategorySchema,
  type SignalCategoryType,
} from './signal-category.registry';

const Category = SignalCategorySchema.enum;
const TargetType = TargetTypeSchema.enum;

// ─── Target type selection ───────────────────────────────────────────────────

const SIGNAL_CATEGORIES_BY_TARGET_TYPE = {
  [TargetType.WEB_APPLICATION]: WEB_APP_SIGNAL_CATEGORIES,
  [TargetType.AI_APPLICATION]: AI_APPLICATION_SIGNAL_CATEGORIES,
  [TargetType.EXTERNAL_NETWORK]: EXTERNAL_NETWORK_SIGNAL_CATEGORIES,
  [TargetType.INTERNAL_NETWORK]: INTERNAL_NETWORK_SIGNAL_CATEGORIES,
} as const satisfies Record<TargetTypeType, readonly SignalCategoryType[]>;

const SIGNAL_CATEGORY_GUARD_BY_TARGET_TYPE = {
  [TargetType.WEB_APPLICATION]: isWebAppCategory,
  [TargetType.AI_APPLICATION]: isAiApplicationSignalCategory,
  [TargetType.EXTERNAL_NETWORK]: isExternalNetworkSignalUiCategory,
  [TargetType.INTERNAL_NETWORK]: isInternalNetworkSignalCategory,
} as const satisfies Record<
  TargetTypeType,
  (category: SignalCategoryType | string) => boolean
>;

export function getSignalCategoriesForTargetType(
  targetType: TargetTypeType,
): readonly SignalCategoryType[] {
  return SIGNAL_CATEGORIES_BY_TARGET_TYPE[targetType];
}

export function isSignalCategoryAllowedForTargetType(
  category: SignalCategoryType | string,
  targetType: TargetTypeType,
): boolean {
  return SIGNAL_CATEGORY_GUARD_BY_TARGET_TYPE[targetType](category);
}

// ─── Classification scopes ───────────────────────────────────────────────────
// Every target type is a scope; the agentic and MCP scopes narrow further.

export const CLASSIFY_SIGNAL_CATEGORY_SCOPES = {
  ...TargetType,
  AGENTIC_AI: 'AGENTIC_AI',
  AGENTIC_AI_FLOW: 'AGENTIC_AI_FLOW',
  AGENTIC_AI_NODE: 'AGENTIC_AI_NODE',
  MCP_SERVER: 'MCP_SERVER',
} as const;

export type ClassifySignalCategoryScope =
  (typeof CLASSIFY_SIGNAL_CATEGORY_SCOPES)[keyof typeof CLASSIFY_SIGNAL_CATEGORY_SCOPES];

const CLASSIFY_SIGNAL_CATEGORY_SCOPE_CHOICES: Record<
  ClassifySignalCategoryScope,
  readonly SignalCategoryType[]
> = {
  ...SIGNAL_CATEGORIES_BY_TARGET_TYPE,
  [CLASSIFY_SIGNAL_CATEGORY_SCOPES.AGENTIC_AI]: [
    ...AGENTIC_AI_SIGNAL_CATEGORIES,
    Category.OTHER,
  ],
  [CLASSIFY_SIGNAL_CATEGORY_SCOPES.AGENTIC_AI_FLOW]: [
    ...AGENTIC_AI_FLOW_SIGNAL_CATEGORIES,
    Category.OTHER,
  ],
  [CLASSIFY_SIGNAL_CATEGORY_SCOPES.AGENTIC_AI_NODE]: [
    ...AGENTIC_AI_NODE_SIGNAL_CATEGORIES,
    Category.OTHER,
  ],
  [CLASSIFY_SIGNAL_CATEGORY_SCOPES.MCP_SERVER]: MCP_SERVER_SIGNAL_CATEGORIES,
};

export function getClassifySignalCategoryChoices(
  categoryScope: ClassifySignalCategoryScope = CLASSIFY_SIGNAL_CATEGORY_SCOPES.WEB_APPLICATION,
  excludeCategories: readonly SignalCategoryType[] = [],
): readonly SignalCategoryType[] {
  return CLASSIFY_SIGNAL_CATEGORY_SCOPE_CHOICES[categoryScope].filter(
    (category) => !excludeCategories.includes(category),
  );
}

export function classifyScopeForTargetType(
  targetType: TargetTypeType,
): ClassifySignalCategoryScope {
  return CLASSIFY_SIGNAL_CATEGORY_SCOPES[targetType];
}

// ─── Flow routing ────────────────────────────────────────────────────────────

export function flowTargetTypeFromSignal({
  category,
  origin,
  targetType,
}: {
  category?: SignalCategoryType | string | null;
  origin?: SignalOriginType | string | null;
  targetType?: TargetTypeType | string | null;
}): TargetTypeType {
  const parsedTargetType = TargetTypeSchema.safeParse(targetType);
  const knownTargetType = parsedTargetType.success
    ? parsedTargetType.data
    : null;

  if (
    knownTargetType === TargetType.EXTERNAL_NETWORK ||
    knownTargetType === TargetType.INTERNAL_NETWORK
  ) {
    return knownTargetType;
  }

  if (origin === SignalOriginSchema.enum.AGENTIC_CODE_TRACKING_ANALYSIS) {
    return TargetType.AI_APPLICATION;
  }

  if (category && isAgenticAiSignalCategory(category)) {
    return TargetType.AI_APPLICATION;
  }

  if (category && isWebAppCategory(category)) {
    // MCP categories that are also web categories stay on the AI target.
    const keepAiTargetType =
      knownTargetType === TargetType.AI_APPLICATION &&
      isMcpServerSignalCategory(category);

    return keepAiTargetType
      ? TargetType.AI_APPLICATION
      : TargetType.WEB_APPLICATION;
  }

  if (
    knownTargetType === TargetType.AI_APPLICATION ||
    knownTargetType === TargetType.WEB_APPLICATION
  ) {
    return knownTargetType;
  }

  return TargetType.WEB_APPLICATION;
}
